import { $restoreEditorState } from '@lexical/utils';
import type { EditorState, LexicalEditor, LexicalNode, RangeSelection } from 'lexical';
import { $getRoot, $getSelection, $isElementNode, $isLineBreakNode, $isRangeSelection, $isTextNode, RootNode } from 'lexical';

/**
 * Deletes up to `count` characters just before the caret, across text and line breaks.
 * - `$trimTextContentFromAnchor` reverts a whole pre-existing text node instead, dropping a paste entirely.
 * - Returns how many characters it couldn't remove (it stops at decorators and block edges).
 */
const $trimBeforeCaret = (selection: RangeSelection, count: number): number => {
  const { anchor } = selection;
  let node: LexicalNode | null = anchor.getNode();
  let offset = anchor.offset;
  if ($isElementNode(node)) {
    node = node.getChildAtIndex(offset - 1);
    offset = node?.getTextContentSize() ?? 0;
  }

  let remaining = count;
  while (remaining > 0 && node) {
    const previous: LexicalNode | null = node.getPreviousSibling();
    if ($isTextNode(node)) {
      const take = Math.min(remaining, offset);
      node.spliceText(offset - take, take, '', true);
      remaining -= take;
    } else if ($isLineBreakNode(node)) {
      node.remove();
      remaining -= 1;
    } else {
      break;
    }
    node = previous;
    offset = previous?.getTextContentSize() ?? 0;
  }
  return remaining;
};

/**
 * Stops the content growing past `max`, counted as the saved value (`root.getTextContent()`).
 * - Typing at the limit is undone; a paste that crosses it keeps only what fits.
 * - Content already over the limit (e.g. an old value) can shrink but not grow.
 * - Returns the unregister function, like Lexical's own `register*` helpers.
 */
export const registerCharacterLimit = (editor: LexicalEditor, max: number): (() => void) => {
  let lastRestored: EditorState | null = null;

  return editor.registerNodeTransform(RootNode, (root) => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;

    const previousState = editor.getEditorState();
    const previousSize = previousState.read(() => $getRoot().getTextContentSize());
    const size = root.getTextContentSize();
    const excess = size - Math.max(max, previousSize);
    if (size <= previousSize || excess <= 0) return;

    // At the limit nothing fits, so undo the edit (keeps the caret); otherwise keep what fits
    const remaining = previousSize >= max ? excess : $trimBeforeCaret(selection, excess);
    // Guarded so a restore can't loop
    if (remaining > 0 && lastRestored !== previousState) {
      lastRestored = previousState;
      $restoreEditorState(editor, previousState);
    }
  });
};
